/* eslint-disable dot-notation */
// ============================================================
// API 回應離線快取（取代 Service Worker 的 networkFirst API cache）
// ------------------------------------------------------------
// 背景：iOS 的 LINE webview 不支援 Service Worker，所以原本 SW 做的 「server 5xx/timeout 時 fallback cache」
//       改成在 axios interceptor 層用 IndexedDB 自己實作（接線在 src/api/offlineApiCache.ts）。
//
// Scope：
//   - 只快取白名單內的 GET 2xx 回應（punch-in-info / shifts / user/profile / school/theme / school-announcements）
//   - 只在「有網路但 server 不穩」(5xx / timeout / Network Error) 時被讀出來當 fallback
//   - 寫入即覆蓋（per URL 永遠只留最新一份）
//
// 多帳號隔離：每筆帶 accountId（= user.profile.id，未登入用 __anon），讀取時用當前 userId 篩；
//   真實 accountId 的 bucket 登出不清、靠 TTL 淘汰；__anon bucket 例外，logout 時呼叫 clearAccount 清掉
//   （避免 user/profile / school/theme 這類 boot 期請求的 __anon 快取被下個 user 撈到）。
// 超過 TTL（預設 7 天，對齊 offlinePunchQueue）的紀錄由 purgeExpired 清掉；另有 MAX_ENTRIES 防爆上限（寫入時從最舊砍起）。
// ============================================================

import { openDB, IDBPDatabase, DBSchema } from 'idb';

export interface CachedApiResponse {
	// 主鍵：accountId + URL，用一個 NUL 分隔符串起來（NUL 不會出現在 accountId 或 url 裡，當分隔符最安全）
	key: string;
	accountId: string;
	// 快取對象的 request URL（含 query string，相對於 axios baseURL）。同一 endpoint 不同 query 各自一筆
	url: string;
	// HTTP status（必為 2xx，存下來是為了 fallback 時組回真實 status）
	status: number;
	// 原始 response headers（putResponse 寫入時會 normalize 成小寫 key）。pagination（x-next-id / x-total-count）靠這個還原
	headers: Record<string, string>;
	// 原始 response body
	data: unknown;
	// 寫入時間 Date.now() ms，用來判斷 TTL
	cachedAt: number;
}

// 對齊 offlinePunchQueue 的 7 天
export const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// 防爆上限（defense-in-depth）。list-only 白名單下穩態 5～10 筆，shifts / school-announcements 的 query 變體最多再加幾筆；超過時依 cachedAt 從最舊砍起
export const MAX_ENTRIES = 30;

// Bootstrap request（school/theme、user/profile）在 user.profile.id 進 Redux 前的 accountId placeholder
export const ANON_ACCOUNT_ID = '__anon';

// ============================================================
// IndexedDB 設定
// ============================================================

const DB_NAME = 'api-response-cache';
// IndexedDB 結構改動時（新增/刪除/修改 object store 或 index）把版本號 +1 並在 upgrade() 補 migration。
// 單純加欄位到 CachedApiResponse 不需要動（IndexedDB 對 record 內容 schemaless）
const DB_VERSION = 1;
const STORE_NAME = 'responses';
const INDEX_BY_CACHED_AT = 'by_cached_at';

interface ApiResponseCacheSchema extends DBSchema {
	[STORE_NAME]: {
		key: string;
		value: CachedApiResponse;
		indexes: { [INDEX_BY_CACHED_AT]: number };
	};
}

// Async singleton：cache Promise 而非 resolved DB，讓並發 caller 共用同一個 pending Promise，只開一次連線
let dbPromise: Promise<IDBPDatabase<ApiResponseCacheSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<ApiResponseCacheSchema>> {
	if (dbPromise) return dbPromise;
	let recoverable: Promise<IDBPDatabase<ApiResponseCacheSchema>>;
	const invalidateConnection = (): void => {
		if (dbPromise === recoverable) dbPromise = null;
		recoverable?.then(db => db.close())['catch'](() => { /* connection 已終止 */ });
	};
	const opening = openDB<ApiResponseCacheSchema>(DB_NAME, DB_VERSION, {
		upgrade(db) {
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
				store.createIndex(INDEX_BY_CACHED_AT, 'cachedAt');
			}
		},
		blocking: invalidateConnection,
		terminated: invalidateConnection,
	});
	// 暫時性 open failure 不可永久 cache rejected Promise；下次 API request 要能重新 open、恢復 cached fallback。
	recoverable = opening['catch'](error => {
		if (dbPromise === recoverable) dbPromise = null;
		throw error;
	});
	dbPromise = recoverable;
	return dbPromise;
}

// 清掉 cached promise，下次 getDb() 重新 open。
export function resetDbForTest(): void {
	dbPromise = null;
}

function buildKey(accountId: string, url: string): string {
	return `${accountId}\u0000${url}`;
}

// ============================================================
// 讀寫
// ============================================================

// 寫入（覆蓋）一筆快取。data 不可結構化複製（DataCloneError）等任何錯誤一律吞掉 —— 快取失敗不該影響正常請求流程
export async function putResponse(input: Omit<CachedApiResponse, 'key' | 'cachedAt'>): Promise<void> {
	try {
		const db = await getDb();
		// header key 入庫前一律 lowercase，避免 fallback 時讀 x-next-id / X-Next-Id 拼錯（axios 預設會 lowercase 但不是合約保證）
		const headers = Object.fromEntries(
			Object.entries(input.headers).map(([k, v]) => [k.toLowerCase(), v]),
		);
		const record: CachedApiResponse = {
			...input,
			headers,
			key: buildKey(input.accountId, input.url),
			cachedAt: Date.now(),
		};
		await db.put(STORE_NAME, record);
		await trimToMaxEntries(db);
	} catch (err) {
		// 寫快取失敗（quota / DataCloneError / DB 開不起來）忽略
	}
}

// 讀一筆快取；找不到或已過 TTL 回 null（過 TTL 的順手刪掉）。任何錯誤一律回 null
export async function getResponse(
	accountId: string,
	url: string,
	now: number = Date.now(),
	ttlMs: number = TTL_MS,
): Promise<CachedApiResponse | null> {
	try {
		const db = await getDb();
		const record = await db.get(STORE_NAME, buildKey(accountId, url));
		if (!record) return null;
		if (now - record.cachedAt > ttlMs) {
			await db.delete(STORE_NAME, record.key);
			return null;
		}
		return record;
	} catch (err) {
		return null;
	}
}

// ============================================================
// 維護
// ============================================================

// 清掉所有 cachedAt 早於 now - ttlMs 的紀錄，回傳刪掉筆數
export async function purgeExpired(now: number = Date.now(), ttlMs: number = TTL_MS): Promise<number> {
	try {
		const db = await getDb();
		const cutoff = now - ttlMs;
		const range = IDBKeyRange.upperBound(cutoff, true);
		const keys = await db.getAllKeysFromIndex(STORE_NAME, INDEX_BY_CACHED_AT, range);
		await Promise.all(keys.map(key => db.delete(STORE_NAME, key)));
		return keys.length;
	} catch (err) {
		return 0;
	}
}

// 清掉指定 accountId bucket 底下的所有快取，回傳刪掉筆數。任何錯誤一律回 0。
// 主要用於登出：__anon bucket 因為跨 user 共用（boot 期請求在 profile.id 進 Redux 前都落在這），
// 不清的話下個 user 進來會在 maybeServeFromCache 的 __anon fallback 路徑撈到上一個 user 的快取。
export async function clearAccount(accountId: string): Promise<number> {
	try {
		const db = await getDb();
		const prefix = `${accountId} `;
		const allKeys = await db.getAllKeys(STORE_NAME);
		const targets = (allKeys as string[]).filter(k => k.startsWith(prefix));
		await Promise.all(targets.map(k => db.delete(STORE_NAME, k)));
		return targets.length;
	} catch (err) {
		return 0;
	}
}

// 超過 MAX_ENTRIES 時依 cachedAt 由舊到新砍掉超出的部分。
// 跟 SW 的 trimCache 一樣是 defense-in-depth 不是嚴格上限：跨多個 mini-transaction，期間若有新寫入可能短暫超標，下次寫入再補。
async function trimToMaxEntries(db: IDBPDatabase<ApiResponseCacheSchema>): Promise<void> {
	const count = await db.count(STORE_NAME);
	if (count <= MAX_ENTRIES) return;
	const keys = await db.getAllKeysFromIndex(STORE_NAME, INDEX_BY_CACHED_AT);
	const overflow = count - MAX_ENTRIES;
	await Promise.all(keys.slice(0, overflow).map(key => db.delete(STORE_NAME, key)));
}
